export type VolumeViewPresetId =
  | 'all'
  | 'autoscaling'
  | 'volumesToActivate'
  | 'volumesPendingSetup'
  | 'unsupported';

export type VolumeViewResolution = VolumeViewPresetId | 'custom';

export interface VolumeViewPreset {
  id: VolumeViewPresetId;
  label: string;
  stateIds: string[];
  eligibilityIds: string[];
}

export const VOLUME_VIEW_PRESETS: VolumeViewPreset[] = [
  {
    id: 'all',
    label: 'All Volumes',
    stateIds: [],
    eligibilityIds: [],
  },
  {
    id: 'autoscaling',
    label: 'Autoscaling Volumes',
    stateIds: ['as_state_on'],
    eligibilityIds: [],
  },
  {
    id: 'volumesToActivate',
    label: 'Volumes to Activate',
    stateIds: ['as_state_off'],
    eligibilityIds: ['as_eligibility_supported'],
  },
  {
    id: 'volumesPendingSetup',
    label: 'Volumes Pending Setup',
    stateIds: ['as_state_off'],
    eligibilityIds: ['as_eligibility_potential'],
  },
  {
    id: 'unsupported',
    label: 'Unsupported Volumes',
    stateIds: [],
    eligibilityIds: ['as_eligibility_unsupported'],
  },
];

export const CUSTOM_VOLUME_VIEW_LABEL = 'Custom';

function normalizeSelection(values: string[]): string[] {
  return [...values].sort();
}

function selectionsMatch(left: string[], right: string[]): boolean {
  const normalizedLeft = normalizeSelection(left);
  const normalizedRight = normalizeSelection(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
}

export function resolveVolumeView(
  stateIds: string[],
  eligibilityIds: string[]
): VolumeViewResolution {
  if (stateIds.length === 0 && eligibilityIds.length === 0) {
    return 'all';
  }

  const matchedPreset = VOLUME_VIEW_PRESETS.find(
    (preset) =>
      preset.id !== 'all' &&
      selectionsMatch(stateIds, preset.stateIds) &&
      selectionsMatch(eligibilityIds, preset.eligibilityIds)
  );

  return matchedPreset?.id ?? 'custom';
}

export function getVolumeViewLabel(resolution: VolumeViewResolution): string {
  if (resolution === 'custom') {
    return CUSTOM_VOLUME_VIEW_LABEL;
  }

  return VOLUME_VIEW_PRESETS.find((preset) => preset.id === resolution)?.label ?? 'All volumes';
}

export function getVolumeViewPreset(presetId: VolumeViewPresetId): VolumeViewPreset {
  return VOLUME_VIEW_PRESETS.find((preset) => preset.id === presetId) ?? VOLUME_VIEW_PRESETS[0];
}
